import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FiX, FiPlus, FiAlertCircle, FiCheckCircle, FiTrash2, FiShield, FiList } from 'react-icons/fi';
import { useI18n } from '../i18n/useI18n';

export function WhitelistModal({ open, onClose, whitelist, onAdd, onRemove }) {
  const { t } = useI18n();
  const [newAddress, setNewAddress] = useState('');
  const [loading, setLoading] = useState(false);

  // 弹窗打开时锁定背景滚动
  useEffect(() => {
    if (!open) return;

    const scrollY = window.scrollY || 0;
    const prevBodyOverflow = document.body.style.overflow;
    const prevBodyPosition = document.body.style.position;
    const prevBodyTop = document.body.style.top;
    const prevBodyWidth = document.body.style.width;
    const prevHtmlOverflow = document.documentElement.style.overflow;

    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
      document.body.style.position = prevBodyPosition;
      document.body.style.top = prevBodyTop;
      document.body.style.width = prevBodyWidth;
      window.scrollTo(0, scrollY);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  // Reset input when modal opens
  useEffect(() => {
    if (open) {
      setNewAddress('');
    }
  }, [open]);

  if (!open) return null;

  const handleAdd = async () => {
    if (!newAddress.trim()) return;
    setLoading(true);
    await onAdd(newAddress);
    setLoading(false);
    setNewAddress('');
  };

  const handleRemove = async (address) => {
    setLoading(true);
    await onRemove(address);
    setLoading(false);
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[210] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-2xl border border-slate-700/60 bg-gradient-to-b from-slate-900/95 to-slate-950/95 backdrop-blur-xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800/50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-violet-500/15 rounded-xl">
              <FiShield className="w-5 h-5 text-violet-400" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">{t('whitelist.title')}</h3>
              <p className="text-xs text-slate-500 uppercase tracking-wider">{t('whitelist.subtitle')}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl bg-slate-800/50 text-slate-400 hover:text-white hover:bg-slate-700/50 transition-colors"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5">
          {/* Info Box */}
          <div className="p-4 bg-violet-500/5 border border-violet-500/20 rounded-xl">
            <div className="flex items-start gap-3">
              <div className="p-1.5 bg-violet-500/15 rounded-lg shrink-0">
                <FiAlertCircle className="w-4 h-4 text-violet-400" />
              </div>
              <div className="text-sm text-slate-300 leading-relaxed">
                <p className="mb-1">{t('whitelist.intro')}</p>
                <p className="text-xs text-slate-500">{t('whitelist.hint')}</p>
              </div>
            </div>
          </div>

          {/* Add Address Input */}
          <div className="space-y-2">
            <label className="text-xs font-bold text-slate-400 uppercase tracking-wider ml-1">
              {t('whitelist.add')}
            </label>
            <div className="flex gap-2">
              <input 
                type="text" 
                value={newAddress}
                onChange={(e) => setNewAddress(e.target.value)}
                placeholder={t('whitelist.placeholder')}
                className="flex-1 bg-slate-950/60 border border-slate-700/60 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20 transition-all font-mono"
              />
              <button 
                onClick={handleAdd}
                disabled={!newAddress || loading}
                className="px-5 py-3 bg-violet-500/15 hover:bg-violet-500/25 text-violet-400 hover:text-violet-300 rounded-xl text-sm font-bold transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed border border-violet-500/30"
              >
                <FiPlus className="w-4 h-4" />
                <span className="hidden sm:inline">{t('whitelist.add')}</span>
              </button>
            </div>
          </div>

          {/* Whitelist Items */}
          <div className="space-y-2">
            <div className="flex items-center justify-between px-1">
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                {t('whitelist.list') || '白名单列表'}
              </label>
              <span className="text-xs text-slate-500">{whitelist.length} {t('whitelist.addresses') || '个地址'}</span>
            </div>
            
            <div className="space-y-2 max-h-[280px] overflow-y-auto custom-scrollbar pr-1">
              {whitelist.length === 0 ? (
                <div className="text-center py-10 bg-slate-800/30 rounded-xl border border-dashed border-slate-700/50 flex flex-col items-center gap-3">
                  <div className="w-14 h-14 rounded-2xl bg-slate-800/50 flex items-center justify-center">
                    <FiList className="w-7 h-7 text-slate-600" />
                  </div>
                  <p className="text-slate-500 text-sm">{t('whitelist.empty')}</p>
                </div>
              ) : (
                whitelist.map((addr) => (
                  <div key={addr} className="flex items-center justify-between p-3.5 bg-slate-800/40 border border-slate-700/50 rounded-xl hover:border-slate-600/80 transition-all group">
                    <div className="flex items-center gap-3 overflow-hidden">
                      <div className="shrink-0 w-9 h-9 rounded-xl bg-violet-500/10 flex items-center justify-center text-violet-400 text-xs font-mono border border-violet-500/20">
                        0x
                      </div>
                      <code className="text-sm text-slate-300 font-mono truncate">{addr}</code>
                    </div>
                    <button 
                      onClick={() => handleRemove(addr)}
                      disabled={loading}
                      className="p-2 text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 rounded-lg transition-all opacity-0 group-hover:opacity-100 disabled:opacity-50"
                      title={t('whitelist.remove')}
                    >
                      <FiTrash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
